import React, { memo, useState, useEffect, useRef, useCallback } from 'react';
import { useAuth } from '../../context/AuthContext';
import { useProject } from '../../context/ProjectContext';
import { useAppVisibility } from '../../hooks/useAppVisibility';
import { ChatMessage, User } from '../../types';
import { db, supabase } from '../../services';
import { generateId, getAvatarUrl, formatTime } from '../../utils/formatters';
import { sanitizeInput } from '../../utils/validation';
import { LoadingSpinner } from '../../components/common';

// ============================================
// Types
// ============================================

interface ChatSectionProps {
  projectId: string;
}

// ============================================
// Component
// ============================================

const ChatSection: React.FC<ChatSectionProps> = memo(({ projectId }) => {
  const { currentUser } = useAuth();
  const { projects, users, addNotification } = useProject();
  const { isVisible, wasHidden, resetWasHidden } = useAppVisibility();

  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [input, setInput] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [showMentions, setShowMentions] = useState(false);
  const [mentionSearch, setMentionSearch] = useState('');
  const [channelStatus, setChannelStatus] = useState<string>('disconnected');

  const scrollRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const channelRef = useRef<ReturnType<typeof supabase.channel> | null>(null);
  const lastRefreshRef = useRef<number>(Date.now());
  const reconnectTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Generate a unique channel ID per tab instance to prevent conflicts
  const tabIdRef = useRef<string>(`tab-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`);

  const project = projects.find(p => p.id === projectId);
  const projectMembers = users.filter(u => project?.memberIds.includes(u.id));

  // Fetch messages on mount and when projectId changes
  useEffect(() => {
    let isMounted = true;

    const fetchMessages = async () => {
      setIsLoading(true);
      try {
        console.log('[Chat] Fetching messages for project:', projectId);
        const cloudMessages = await db.getMessages(projectId);
        if (isMounted) {
          setMessages(cloudMessages);
          lastRefreshRef.current = Date.now();
        }
      } catch (err: any) {
        console.error('Cloud Chat Fetch Error:', err);
      } finally {
        if (isMounted) {
          setIsLoading(false);
        }
      }
    };

    fetchMessages();

    return () => {
      isMounted = false;
    };
  }, [projectId]);

  // Auto-reconnection effect for chat channel
  useEffect(() => {
    if (channelStatus === 'reconnecting' || channelStatus === 'error' || channelStatus === 'closed') {
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
      }

      reconnectTimeoutRef.current = setTimeout(() => {
        console.log('[Chat] Executing scheduled reconnection...');

        if (channelRef.current) {
          supabase.removeChannel(channelRef.current);
          channelRef.current = null;
        }

        setChannelStatus('connecting');
      }, 1000);

      return () => {
        if (reconnectTimeoutRef.current) {
          clearTimeout(reconnectTimeoutRef.current);
        }
      };
    }
  }, [channelStatus]);

  // Real-time subscription for chat messages
  useEffect(() => {
    // Don't create a new channel if one already exists and is connected
    if (channelRef.current && channelRef.current.state === 'joined') {
      console.log('[Chat] Realtime channel already connected, skipping setup');
      return;
    }

    // Only create channel when we need to connect/reconnect
    if (channelStatus !== 'connecting' && channelStatus !== 'disconnected') {
      console.log('[Chat] Channel status is', channelStatus, '- waiting for connecting/disconnected state');
      return;
    }

    console.log('[Chat] Setting up realtime subscription for project:', projectId, 'tab:', tabIdRef.current);

    // Real-time subscription - use unique channel per tab to prevent conflicts
    const channel = supabase
      .channel(`chat_${projectId}_${tabIdRef.current}`)
      .on(
        'postgres_changes',
        { event: 'INSERT', schema: 'public', table: 'messages', filter: `project_id=eq.${projectId}` },
        (payload) => {
          const raw = payload.new as any;
          const newMessage: ChatMessage = {
            id: raw.id,
            projectId: raw.project_id,
            senderId: raw.user_id,
            content: raw.text,
            timestamp: Number(raw.timestamp)
          };

          setMessages(prev => {
            const exists = prev.some(m => m.id === newMessage.id);
            if (exists) return prev;
            return [...prev, newMessage];
          });
        }
      )
      .subscribe((status) => {
        console.log('[Chat] Subscription status:', status);

        if (status === 'SUBSCRIBED') {
          console.log('[Chat] Successfully subscribed to chat updates');
          // DON'T call setChannelStatus here - it causes the effect to re-run and cleanup
        } else if (status === 'CHANNEL_ERROR' || status === 'TIMED_OUT') {
          setChannelStatus('error');
        } else if (status === 'CLOSED') {
          setChannelStatus('closed');
          if (channelRef.current === channel) {
            channelRef.current = null;
          }
        }
      });

    // Store channel reference
    channelRef.current = channel;

    return () => {
      if (channelRef.current === channel) {
        channelRef.current = null;
      }
      supabase.removeChannel(channel);
    };
  }, [projectId, channelStatus]); // Added channelStatus to allow reconnection when status changes

  // Handle app becoming visible - refresh messages
  useEffect(() => {
    if (isVisible && wasHidden) {
      const timeSinceLastRefresh = Date.now() - lastRefreshRef.current;
      const DEBOUNCE_TIME = 1000; // Reduced to 1 second

      if (timeSinceLastRefresh > DEBOUNCE_TIME) {
        console.log('[Chat] App became visible, refreshing messages...');

        // Refresh messages
        db.getMessages(projectId).then(cloudMessages => {
          setMessages(cloudMessages);
          lastRefreshRef.current = Date.now();
        }).catch(err => {
          console.error('Chat refresh error:', err);
        });

        // Reconnect channel if needed
        if (channelRef.current) {
          const currentChannelStatus = channelRef.current.state;
          if (currentChannelStatus !== 'joined') {
            console.log('[Chat] Forcing channel reconnection...');
            setChannelStatus('reconnecting');
          }
        }

        // Reset wasHidden flag after processing
        resetWasHidden();
      }
    }
  }, [isVisible, wasHidden, projectId, resetWasHidden]);

  // Auto-scroll to bottom when new messages arrive
  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages]);

  // Handle input change with mention detection
  const handleInputChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    const cursorPosition = e.target.selectionStart || 0;
    const textBeforeCursor = value.substring(0, cursorPosition);
    const lastAtSymbol = textBeforeCursor.lastIndexOf('@');

    if (lastAtSymbol !== -1 && !textBeforeCursor.substring(lastAtSymbol).includes(' ')) {
      const query = textBeforeCursor.substring(lastAtSymbol + 1);
      setMentionSearch(query);
      setShowMentions(true);
    } else {
      setShowMentions(false);
    }
    setInput(value);
  }, []);

  // Insert mention into input
  const insertMention = useCallback((user: User) => {
    const cursorPosition = inputRef.current?.selectionStart || 0;
    const lastAt = input.lastIndexOf('@', cursorPosition - 1);
    const textBeforeAt = input.substring(0, lastAt);
    const textAfterAt = input.substring(cursorPosition);
    const newValue = `${textBeforeAt}@${user.name} ${textAfterAt}`;
    
    setInput(newValue);
    setShowMentions(false);
    inputRef.current?.focus();
  }, [input]);

  // Send message
  const handleSend = useCallback(async (e: React.FormEvent) => {
    e.preventDefault();
    if (!input.trim() || !currentUser) return;

    const content = sanitizeInput(input);
    const newMessage: ChatMessage = {
      id: generateId(),
      projectId,
      senderId: currentUser.id,
      content,
      timestamp: Date.now()
    };

    // Optimistic update
    setMessages(prev => [...prev, newMessage]);
    setInput('');
    setShowMentions(false);

    try {
      await db.addMessage(newMessage);
      
      // Send notifications for mentions with push notification
      projectMembers.forEach(member => {
        if (content.includes(`@${member.name}`) && member.id !== currentUser.id) {
          addNotification(
            '💬 New Mention', 
            `${currentUser.name} mentioned you in ${project?.name}`,
            member.id,
            `/projects/${projectId}?tab=chat`,
            true // Send push notification
          );
        }
      });
    } catch (err) {
      console.error('Message Sync Failed:', err);
      // Rollback on error
      setInput(content);
      setMessages(prev => prev.filter(m => m.id !== newMessage.id));
    }
  }, [input, currentUser, projectId, projectMembers, project?.name, addNotification]);

  // Render message content with highlighted mentions
  const renderMessageContent = useCallback((content: string) => {
    const parts = content.split(/(@[a-zA-Z0-9 ]+)/g);
    return parts.map((part, i) => {
      if (part.startsWith('@')) {
        const name = part.substring(1).trim();
        const isMember = projectMembers.some(m => m.name === name);
        if (isMember) {
          return (
            <span key={i} className="font-black text-indigo-200 bg-indigo-500/30 px-1.5 py-0.5 rounded-md mx-0.5">
              @{name}
            </span>
          );
        }
      }
      return part;
    });
  }, [projectMembers]);

  const filteredMembers = projectMembers.filter(m => 
    m.name.toLowerCase().includes(mentionSearch.toLowerCase())
  );

  return (
    <div className="flex flex-col h-full bg-white rounded-[2rem] border border-slate-100 overflow-hidden shadow-sm">
      {/* Messages */}
      <div ref={scrollRef} className="flex-1 overflow-y-auto p-6 space-y-6 bg-slate-50/30 no-scrollbar">
        {isLoading ? (
          <div className="flex items-center justify-center h-full">
            <LoadingSpinner size="md" />
          </div>
        ) : messages.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-full text-slate-400 opacity-60">
            <div className="w-16 h-16 bg-slate-100 rounded-3xl flex items-center justify-center mb-4">
              <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z"/>
              </svg>
            </div>
            <p className="text-xs font-black uppercase tracking-widest text-center">
              Cloud Archive Active<br/>Start a conversation
            </p>
          </div>
        ) : (
          messages.map(msg => {
            const isMe = msg.senderId === currentUser?.id;
            const sender = users.find(u => u.id === msg.senderId);
            
            return (
              <div key={msg.id} className={`flex gap-3 animate-in slide-in-from-bottom-2 ${isMe ? 'flex-row-reverse' : 'flex-row'}`}>
                <img 
                  src={sender?.avatar || getAvatarUrl(msg.senderId)} 
                  className="w-9 h-9 rounded-2xl shadow-sm border border-white" 
                  alt=""
                  loading="lazy"
                />
                <div className="max-w-[80%] group">
                  {!isMe && (
                    <p className="text-[10px] font-black text-slate-400 mb-1 ml-1 uppercase tracking-tighter">
                      {sender?.name || 'Unknown User'}
                    </p>
                  )}
                  <div className={`p-4 rounded-[1.5rem] text-sm leading-relaxed shadow-sm ${
                    isMe 
                      ? 'bg-indigo-600 text-white rounded-tr-none shadow-indigo-200' 
                      : 'bg-white text-slate-800 rounded-tl-none border border-slate-100'
                  }`}>
                    {renderMessageContent(msg.content)}
                  </div>
                  <p className={`text-[9px] text-slate-400 mt-1.5 font-bold uppercase tracking-widest ${isMe ? 'text-right mr-1' : 'ml-1'}`}>
                    {formatTime(msg.timestamp)}
                  </p>
                </div>
              </div>
            );
          })
        )}
      </div>

      {/* Input */}
      <div className="p-4 bg-white border-t border-slate-50 relative">
        {/* Mentions Dropdown */}
        {showMentions && filteredMembers.length > 0 && (
          <div className="absolute bottom-full left-4 right-4 mb-2 bg-white rounded-2xl border border-slate-100 shadow-2xl overflow-hidden animate-in slide-in-from-bottom-4 duration-200 z-50">
            <div className="p-3 bg-slate-50 border-b border-slate-100">
              <span className="text-[10px] font-black uppercase tracking-widest text-slate-400">
                Mention Project Member
              </span>
            </div>
            <div className="max-h-48 overflow-y-auto no-scrollbar">
              {filteredMembers.map(member => (
                <button
                  key={member.id}
                  onClick={() => insertMention(member)}
                  className="w-full flex items-center gap-3 p-3 hover:bg-indigo-50 transition-colors text-left group"
                >
                  <img src={member.avatar} className="w-8 h-8 rounded-xl border border-white shadow-sm" alt="" />
                  <div>
                    <p className="text-sm font-bold text-slate-900 group-hover:text-indigo-600">{member.name}</p>
                    <p className="text-[10px] text-slate-400 font-medium">{member.role.toLowerCase()}</p>
                  </div>
                </button>
              ))}
            </div>
          </div>
        )}

        <form onSubmit={handleSend} className="flex gap-3">
          <input 
            ref={inputRef}
            type="text" 
            value={input}
            onChange={handleInputChange}
            placeholder="Type a message or use @ to mention..."
            className="flex-1 px-6 py-4 bg-slate-50/50 border border-slate-200 rounded-2xl text-sm outline-none focus:bg-white focus:border-indigo-500 focus:ring-4 focus:ring-indigo-500/5 transition-all placeholder-slate-400 font-semibold text-slate-900"
          />
          <button 
            type="submit"
            disabled={!input.trim()}
            className="px-6 bg-indigo-600 text-white font-black uppercase tracking-widest text-[10px] rounded-2xl hover:bg-indigo-700 transition-all shadow-xl shadow-indigo-600/20 active:scale-95 disabled:opacity-30"
          >
            Send
          </button>
        </form>
      </div>
    </div>
  );
});

ChatSection.displayName = 'ChatSection';

export default ChatSection;
